import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TonelagExplorer from "./TonelagExplorerLazy";
import {
  buildTonelagHeaders,
  normalizeTonelagApiBase,
  TONELAG_API_BASE_URL,
} from "./tonelagApiFinal";

async function readJsonResponse(response) {
  let payload = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail =
      payload?.detail ??
      payload?.message ??
      `${response.status} ${response.statusText}`;

    throw new Error(
      typeof detail === "string"
        ? detail
        : JSON.stringify(detail, null, 2)
    );
  }

  return payload;
}

export default function TonelagExplorerMongoLazy({
  apiBaseUrl = TONELAG_API_BASE_URL,
  authToken = null,
  datasetId = "bokmaalets-tonelagspar",
  summaryPageSize = 500,
  credentials = "omit",
  requestHeaders = null,
  persistReviews = true,
  scanImageBasePath = null,
  getScanImageUrl = null,
  getSourcePageUrl = null,
  title = "Bokmålets tonelagspar",
}) {
  const [loadState, setLoadState] = useState({
    state: "loading",
    loaded: 0,
    total: 0,
    message: "Loading dataset metadata…",
  });
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [recordDetailsById, setRecordDetailsById] = useState({});
  const [recordDetailsLoadingById, setRecordDetailsLoadingById] =
    useState({});
  const [recordDetailsErrorsById, setRecordDetailsErrorsById] =
    useState({});

  const [reviewSave, setReviewSave] = useState({
    state: "idle",
    message: "",
  });

  const detailsRef = useRef({});
  const requestsInFlightRef = useRef(new Set());

  const api = useMemo(
    () => normalizeTonelagApiBase(apiBaseUrl),
    [apiBaseUrl]
  );

  const headers = useMemo(
    () =>
      buildTonelagHeaders({
        authToken,
        extraHeaders: requestHeaders,
      }),
    [authToken, requestHeaders]
  );

  const reload = useCallback(() => {
    detailsRef.current = {};
    requestsInFlightRef.current.clear();
    setRecordDetailsById({});
    setRecordDetailsLoadingById({});
    setRecordDetailsErrorsById({});
    setReloadKey((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadSummaries() {
      setData(null);
      setLoadState({
        state: "loading",
        loaded: 0,
        total: 0,
        message: "Loading dataset metadata…",
      });

      try {
        const metadataResponse = await fetch(
          `${api}/datasets/${encodeURIComponent(datasetId)}/metadata`,
          {
            credentials,
            headers,
            signal: controller.signal,
          }
        );
        const metadataPayload = await readJsonResponse(metadataResponse);

        const total = Number(metadataPayload.imported_records ?? 0);
        const safePageSize = Math.max(
          1,
          Math.min(1000, Number(summaryPageSize) || 500)
        );

        const summaries = [];
        let skip = 0;

        setLoadState({
          state: "loading",
          loaded: 0,
          total,
          message: `Loading 0 of ${total.toLocaleString()} summaries…`,
        });

        while (skip < total) {
          const response = await fetch(
            `${api}/datasets/${encodeURIComponent(
              datasetId
            )}/summaries?skip=${skip}&limit=${safePageSize}`,
            {
              credentials,
              headers,
              signal: controller.signal,
            }
          );
          const payload = await readJsonResponse(response);

          summaries.push(...(payload.items ?? []));

          if (!active) return;

          setLoadState({
            state: "loading",
            loaded: summaries.length,
            total: payload.total ?? total,
            message:
              `Loading ${summaries.length.toLocaleString()} of ` +
              `${Number(payload.total ?? total).toLocaleString()} summaries…`,
          });

          if (payload.next_skip == null) break;
          skip = payload.next_skip;
        }

        if (!active) return;

        setData({
          ...(metadataPayload.metadata ?? {}),
          pair_candidates: summaries,
          mongo_dataset: {
            dataset_id: datasetId,
            record_shape: "summary",
            status: metadataPayload.status,
            expected_records: metadataPayload.expected_records,
            imported_records: metadataPayload.imported_records,
            updated_at: metadataPayload.updated_at,
          },
        });

        setLoadState({
          state: "ready",
          loaded: summaries.length,
          total: summaries.length,
          message:
            `Loaded ${summaries.length.toLocaleString()} compact summaries.`,
        });
      } catch (error) {
        if (controller.signal.aborted) return;

        setLoadState({
          state: "error",
          loaded: 0,
          total: 0,
          message:
            error instanceof Error
              ? error.message
              : "Could not load tonelag summaries.",
        });
      }
    }

    loadSummaries();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    api,
    credentials,
    datasetId,
    headers,
    reloadKey,
    summaryPageSize,
  ]);

  const loadRecordDetail = useCallback(
    async (recordId) => {
      if (!recordId) return;
      if (detailsRef.current[recordId]) return;
      if (requestsInFlightRef.current.has(recordId)) return;

      requestsInFlightRef.current.add(recordId);

      setRecordDetailsLoadingById((current) => ({
        ...current,
        [recordId]: true,
      }));
      setRecordDetailsErrorsById((current) => ({
        ...current,
        [recordId]: null,
      }));

      try {
        const response = await fetch(
          `${api}/datasets/${encodeURIComponent(
            datasetId
          )}/records/${encodeURIComponent(recordId)}`,
          {
            credentials,
            headers,
          }
        );
        const record = await readJsonResponse(response);

        detailsRef.current[recordId] = record;
        setRecordDetailsById((current) => ({
          ...current,
          [recordId]: record,
        }));
      } catch (error) {
        setRecordDetailsErrorsById((current) => ({
          ...current,
          [recordId]:
            error instanceof Error
              ? error.message
              : `Could not load record ${recordId}.`,
        }));
      } finally {
        requestsInFlightRef.current.delete(recordId);
        setRecordDetailsLoadingById((current) => ({
          ...current,
          [recordId]: false,
        }));
      }
    },
    [api, credentials, datasetId, headers]
  );

  const saveReview = useCallback(
    async ({ pairId, review }) => {
      if (!persistReviews) return;

      const writeHeaders = buildTonelagHeaders({
        authToken,
        json: true,
        extraHeaders: requestHeaders,
      });

      setReviewSave({
        state: "saving",
        message: `Saving review for ${pairId}…`,
      });

      try {
        const response = await fetch(
          `${api}/datasets/${encodeURIComponent(
            datasetId
          )}/records/${encodeURIComponent(pairId)}/review`,
          {
            method: "PATCH",
            credentials,
            headers: writeHeaders,
            body: JSON.stringify({ review }),
          }
        );
        await readJsonResponse(response);

        const existing = detailsRef.current[pairId];
        if (existing) {
          const updated = {
            ...existing,
            research_review: review,
          };
          detailsRef.current[pairId] = updated;
          setRecordDetailsById((current) => ({
            ...current,
            [pairId]: updated,
          }));
        }

        setReviewSave({
          state: "saved",
          message: `Review saved for ${pairId}.`,
        });
      } catch (error) {
        setReviewSave({
          state: "error",
          message:
            error instanceof Error
              ? error.message
              : `Could not save review for ${pairId}.`,
        });
      }
    },
    [
      api,
      authToken,
      credentials,
      datasetId,
      persistReviews,
      requestHeaders,
    ]
  );

  if (loadState.state === "loading") {
    const percentage =
      loadState.total > 0
        ? Math.round((100 * loadState.loaded) / loadState.total)
        : 0;

    return (
      <div className="min-h-screen bg-[#f3f0e7] p-6 text-slate-900">
        <div className="mx-auto max-w-xl rounded-2xl border border-stone-300 bg-white p-6 shadow-sm">
          <div className="text-sm font-semibold">
            Loading compact tonelag index
          </div>
          <div className="mt-1 text-sm text-slate-600">
            {loadState.message}
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-indigo-600 transition-all"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loadState.state === "error") {
    return (
      <div className="min-h-screen bg-[#f3f0e7] p-6 text-slate-900">
        <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-950 shadow-sm">
          <div className="font-semibold">
            Could not load MongoDB summaries
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {loadState.message}
          </p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 rounded-lg bg-rose-800 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-900"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {reviewSave.state !== "idle" ? (
        <div
          className={[
            "fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-full border px-4 py-2 text-xs font-semibold shadow-lg",
            reviewSave.state === "error"
              ? "border-rose-300 bg-rose-700 text-white"
              : reviewSave.state === "saved"
              ? "border-emerald-300 bg-emerald-700 text-white"
              : "border-sky-300 bg-white text-sky-900",
          ].join(" ")}
          role={reviewSave.state === "error" ? "alert" : "status"}
        >
          {reviewSave.message}
        </div>
      ) : null}

      <TonelagExplorer
        data={data}
        title={title}
        scanImageBasePath={scanImageBasePath}
        getScanImageUrl={getScanImageUrl}
        getSourcePageUrl={getSourcePageUrl}
        onReviewChange={persistReviews ? saveReview : null}
        enableMongoTransfer={false}
        recordDetailsById={recordDetailsById}
        recordDetailsLoadingById={recordDetailsLoadingById}
        recordDetailsErrorsById={recordDetailsErrorsById}
        onRequestRecordDetail={loadRecordDetail}
      />
    </>
  );
}
